import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react"
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap"
import Select from "react-select"
import Switch from "react-switch";
import { useFormik } from "formik"
import { apiClient } from "infra/api-client";
import { BASE_URL } from "../../global"
import { toast } from "react-toastify"

interface CopyLessonActivitiesDialogProps {}

interface Input {
  session: InputSession
}

interface InputSession {
  tbl_session_time: InputSessionTime
  pk_ses_key: string;

}

interface InputSessionTime {
  tbl_student_enrolment: InputEnrolment[]
}

interface InputEnrolment {
  tbl_student: InputStudent
}

interface InputStudent {
  pk_student_key: string;
  st_first_name: string;
  st_surname: string;
  Lesson: InputLesson;
}

interface InputLesson {
  pk_lesson_key: string;
}

interface CopyParams {
  fromStudent?: FromStudentOption
  noSelectedStudents?: string
}

interface FromStudentOption {
  label: string;
  value: InputStudent
}

interface ToStudentOption {
  label: string;
  student: InputStudent
  selected: boolean
}

export const CopyLessonActivitiesDialog = forwardRef((props: CopyLessonActivitiesDialogProps, ref) => {

  const [open, setOpen] = useState(false);
  const [input, setInput] = useState<Input>();
  const [fromOptions, setFromOptions] = useState<FromStudentOption[]>([]);
  const [toOptions, setToOptions] = useState<ToStudentOption[]>([]);
  const [toAll, setToAll] = useState<boolean>(true);
  const formRef = useRef<HTMLFormElement>(null);
  const [resolver, setResolver] = useState<(value: boolean)=>void>();
  const [confirmationData, setConfirmationData] = useState<{sourceStudent: InputStudent, targetStudents: InputStudent[]}>();
  const [confirmation, setConfirmation] = useState<boolean>(false);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);


  const validator = (values: CopyParams)=>{

    const shouldValidate = formik.submitCount > 0 || formik.dirty;
    if(!shouldValidate) {
      return {}
    }

    const errors: Record<string, any> = {}

    if(!values.fromStudent) {
      errors.fromStudent = 'Please select a student to copy from';
    }

    const selectedStudents = toOptions.filter(e=>e.student.pk_student_key !== values.fromStudent?.value.pk_student_key)
      .filter( e=> e.selected);

    if(selectedStudents.length === 0) {
      errors.noSelectedStudents = 'Please select at least one student to copy to';
    }

    return errors;
  }

  const onSubmit = async (values: CopyParams)=>{

    const sourceStudent =  values.fromStudent!.value;
    const sourceStudentKey = sourceStudent!.pk_student_key;
    const selectedStudents = toOptions.filter(e=>e.student.pk_student_key !== values.fromStudent?.value.pk_student_key)
      .filter( e=> e.selected);
    const targetStudentKeys = selectedStudents.map(e=>e.student.pk_student_key);
    const sessionKey = input?.session.pk_ses_key;
    const sourceLessonKey = sourceStudent.Lesson.pk_lesson_key;

    const copyLessonRequestParams = {
      sourceStudentKey: sourceStudentKey,
      sourceLessonKey: sourceLessonKey,
      sessionKey: sessionKey,
      targetStudentKeys: targetStudentKeys
    }

    setConfirmationData({
      sourceStudent: sourceStudent,
      targetStudents: selectedStudents.map(e=>e.student)
    })

    setOpenConfirmation(true);
  }

  const doSubmit = async ()=>{

    if(!confirmationData)
      return;

    const sourceStudent =  confirmationData.sourceStudent;
    const sourceStudentKey = sourceStudent.pk_student_key;
    const selectedStudents = confirmationData.targetStudents;
    const targetStudentKeys = selectedStudents.map(e=>e.pk_student_key);
    const sessionKey = input?.session.pk_ses_key;
    const sourceLessonKey = sourceStudent.Lesson.pk_lesson_key;

    const copyLessonRequestParams = {
      sourceStudentKey: sourceStudentKey,
      sourceLessonKey: sourceLessonKey,
      sessionKey: sessionKey,
      targetStudentKeys: targetStudentKeys
    }

    let success = false;
    try {
      setSubmitting(true);
      const apiResult = await apiClient.post(BASE_URL + "activity/copyLessonPlan", copyLessonRequestParams);
      if(apiResult && apiResult.data) {
        success = true;
      }
    }
    catch (e) {
      toast.error("Unable to copy activities", { autoClose: 3000, })
    }
    finally {
      setSubmitting(false);
      setOpenConfirmation(false);
    }

    if(success) {
      console.info('success')
      setOpen(false);
      setShowSuccess(true);
    }

  }

  const formik = useFormik<CopyParams>({
    initialValues: {
    },
    validate: validator,
    onSubmit: onSubmit
  });

  const openDialog = (input: Input)=>{
    setInput(input);
    setOpen(true);

    const result = new Promise(resolve => {
      setResolver(()=> resolve)
    });

    return result;
  }

  const setupFromOption = ()=>{
    const enrolments = input?.session.tbl_session_time.tbl_student_enrolment;
    const options: FromStudentOption[] = []
    enrolments?.forEach( e=>{
      const student = e.tbl_student;
      options.push({
        label: `${student.st_first_name} ${student.st_surname}`,
        value: student
      })
    })
    setFromOptions(options)
  }

  const setupToOptions = ()=>{
    const enrolments = input?.session.tbl_session_time.tbl_student_enrolment;
    const options: ToStudentOption[] = []
    enrolments?.forEach( e=>{
      const student = e.tbl_student;
      options.push({
        label: `${student.st_first_name} ${student.st_surname}`,
        student: student,
        selected: true,
      })
    })
    setToOptions(options)
  }

  const reset = ()=>{
    setupFromOption();
    setupToOptions();
    formik.setValues({})
    formik.setErrors({});
  }

  useEffect(() => {
    reset();
  }, [input])

  const cancel = ()=>{
    setOpen(false);
    if(resolver) {
      resolver(false)
    }
  }

  useImperativeHandle(ref, () => ({
    open: openDialog,
  }));

  const studentStyle: React.CSSProperties = {
    fontWeight: 'bold',
  }

  return (
    <>
      <Modal isOpen={open} unmountOnClose={true} centered={true} size={'lg'}
             style={{fontSize: '16pt'}}
      >
        <ModalHeader className="mt-0 text-dark">
          Copy Lesson Activities
        </ModalHeader>

        <ModalBody>
          <form onSubmit={formik.handleSubmit} ref={formRef}>

            <div>Copy activities from student</div>

            <div className={"mt-20"}>
              <Select name="fromStudent" isMulti={false} options={fromOptions}
                      value={formik.values.fromStudent}
                      onChange={ val=>formik.setFieldValue('fromStudent', val)}>
              </Select>
            </div>

            <div className={"mt-50"}>
              <ErrorMsg message={formik.errors.fromStudent}/>

              {formik.values.fromStudent?.value &&
                <>

                  <ErrorMsg message={formik.errors.noSelectedStudents}/>

                  <div>
                    Copy activities to student
                  </div>

                  <div className={"mt-20"}>
                    <Switch checked={toAll} onColor={'#ff0000'}
                      onChange={ val=>{
                        setToAll(val)
                        toOptions.forEach( e=>{
                          e.selected=val;
                        })
                      }}/> <span style={{verticalAlign: 'top'}}>All Student</span>

                  </div>
                  {
                    toOptions.filter( e=>  e.student.pk_student_key !== formik.values.fromStudent?.value.pk_student_key)
                      .map( option => <div className={"mt-20"}>
                          <Switch disabled={toAll} checked={option.selected} onColor={'#ff0000'}
                                  onChange={(val)=>{
                            setToOptions([...toOptions])
                            option.selected = val;
                                  }}/> <span style={{verticalAlign: 'top'}}>{option.student.st_first_name} {option.student.st_surname}</span>
                        </div>
                      )
                  }
                </>
              }
            </div>

          </form>
        </ModalBody>

        <ModalFooter>
            <button type="button" onClick={()=>{
              formik.submitCount++;
              formRef.current?.requestSubmit()}
            }
                    className={'btn btn-danger text-white'}
            >
              Copy Activities
            </button>
            <button type="button" onClick={cancel}
                    className={'btn btn-danger text-white bg-dark border-dark'}
            >Cancel</button>
        </ModalFooter>
      </Modal>


      <Modal style={{
        fontSize: '16pt',
      }} isOpen={openConfirmation} unmountOnClose={true} centered={true} size={'lg'}>
        <ModalHeader>
          Confirmation
        </ModalHeader>

        <ModalBody>
          Are you sure you want to copy activities from
          <span style={studentStyle}> {confirmationData?.sourceStudent.st_first_name} {confirmationData?.sourceStudent.st_surname} </span>
          to

          <ul className={'mt-3'}>
            {confirmationData?.targetStudents.map( target => <>
              <li><span style={studentStyle}>{target.st_first_name} {target.st_surname}</span></li>
            </>)}
          </ul>

          This will delete the any current activities from the student before copying

        </ModalBody>

        <ModalFooter>
          <button type="button" className={'btn btn-danger text-white'}
                  onClick={()=>{
                    doSubmit();
                  }}
          >
            Yes. Delete and then copy
          </button>
          <button type="button" className={'btn btn-danger text-white bg-dark border-dark'}
                  onClick={()=>{
                    setConfirmationData(null!)
                    setOpenConfirmation(false);
                  }}
          >Cancel</button>
        </ModalFooter>
      </Modal>


      <Modal style={{fontSize: '16pt'}}
        size={'lg'} isOpen={showSuccess} centered={true}>
        <ModalBody>
          Activities successfully copied. Click close to reload the page
        </ModalBody>
        <ModalFooter>
          <button type="button" className={'btn btn-danger text-white'}
                  onClick={()=>{
                    setShowSuccess(false);
                    if(resolver) {
                      resolver(true)
                    }
                  }}
          >
            Close
          </button>
        </ModalFooter>
      </Modal>
    </>
  )
})

const ErrorMsg = (props: {message?: string})=>{

  return (
    <>
      {props.message &&
        <div style={{
          color: '#ff0000'
        }}>{props.message}</div>
      }
    </>
  )
}